import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { translate } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  GridState,
  IRowNode,
  RowSelectedEvent,
  RowSelectionOptions,
  SelectionColumnDef,
  SideBarDef,
  StateUpdatedEvent,
  StatusPanelDef,
} from 'ag-grid-enterprise';

import { SharedTranslocoModule } from '@schaeffler/transloco';

import {
  getSelectedCalculation,
  getSelectedReferenceTypeIdentifiers,
} from '@cdba/compare/store';
import {
  DEFAULT_COLUMN_DEFINITION,
  DEFAULT_SELECTION_COLUMN_DEFINITION,
} from '@cdba/shared/constants/grid-config';
import { COMPARE_ITEMS_MAX_COUNT } from '@cdba/shared/constants/table';
import { arrayEquals } from '@cdba/shared/utils';

import { Calculation } from '../../models';
import { AgGridStateService } from '../../services';
import { getMainMenuItems } from '../table';
import { NoRowsParams } from '../table/custom-overlay/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { SidebarService } from '../table/side-bar/side-bar.service';
import { CalculationsStatusBarModule } from '../table/status-bar/calculations-status-bar';
import {
  ColumnDefinitionService,
  FRAMEWORK_COMPONENTS_DEFAULT,
  FRAMEWORK_COMPONENTS_MINIFIED,
  STATUS_BAR_CONFIG,
} from './config';
import { SelectedCalculationsEvent } from './model/calculation-selection-event.model';

@Component({
  selector: 'cdba-calculations-table',
  templateUrl: './calculations-table.component.html',
  imports: [
    CommonModule,
    CalculationsStatusBarModule,
    SharedTranslocoModule,
    AgGridModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ColumnDefinitionService],
})
export class CalculationsTableComponent implements OnInit {
  private readonly agGridStateService = inject(AgGridStateService);
  private readonly columnDefinitionService = inject(ColumnDefinitionService);
  private readonly sidebarService = inject(SidebarService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  @Input() index: number = undefined;
  @Input() minified = false;
  @Input() rowSelectionType: 'singleRow' | 'multiRow';
  @Input() enableSelectionWithoutKeys = false;
  @Input() rowData: Calculation[];
  @Input() selectedNodeIds: string[];
  @Input() isLoading: boolean;
  @Input() errorMessage: string;

  @Output() readonly selectionChange: EventEmitter<
    SelectedCalculationsEvent[]
  > = new EventEmitter();

  modules: any[];
  components: any;
  sideBar: SideBarDef;
  cellSelection: boolean;
  statusBar: {
    statusPanels: StatusPanelDef[];
  };

  defaultColDef: ColDef = DEFAULT_COLUMN_DEFINITION;
  selectionColumnDef: SelectionColumnDef = DEFAULT_SELECTION_COLUMN_DEFINITION;
  columnDefs: ColDef[] = this.columnDefinitionService.COLUMN_DEFINITION;
  initialState?: GridState;
  rowSelection: RowSelectionOptions;
  disabledCalculation: Calculation;
  sameReferenceTypesSelected = false;

  noRowsOverlayComponentParams: NoRowsParams = {
    getMessage: () => {
      if (this.errorMessage !== undefined && this.errorMessage !== '') {
        return this.errorMessage;
      }

      if (this.rowData === undefined || this.rowData.length === 0) {
        return translate(`shared.calculations.noCalculationsToDisplay`);
      }

      return '';
    },
  };

  loadingOverlayComponent = 'customLoadingOverlay';
  noRowsOverlayComponent = 'customNoRowsOverlay';
  radioButtonCellRenderComponent = 'radioButtonCellRenderComponent';

  rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never';

  getMainMenuItems = getMainMenuItems;

  private gridApi: GridApi;
  private storageKey: string;

  ngOnInit(): void {
    this.storageKey = this.minified
      ? AgGridStateService.CALCULATIONS_TABLE_MINIFIED_GRID_STATE_KEY
      : AgGridStateService.CALCULATIONS_TABLE_DEFAULT_GRID_STATE_KEY;
    this.setTableProperties(this.minified);

    this.trackSelectedCalculation();

    const gridState = this.agGridStateService.getGridState(this.storageKey);

    if (gridState) {
      this.initialState = gridState;
    }
  }

  onRowSelected(evt: RowSelectedEvent): void {
    setTimeout(() => {
      // Ignore programatical row selections
      if (evt.source === 'api') {
        return;
      }

      const node = evt.node;
      const api = evt.api;

      const maxLength = this.minified ? 1 : COMPARE_ITEMS_MAX_COUNT;

      const previouslySelectedRows = this.selectedNodeIds
        ? [...this.selectedNodeIds]
        : [];

      const { id } = node;
      const selected = node.isSelected();

      const newSelectedRows = selected
        ? [...new Set(this.selectedNodeIds).add(id)]
        : previouslySelectedRows.filter((entry: string) => entry !== id);

      if (newSelectedRows.length > maxLength) {
        const latestSelectedNode = api.getRowNode(newSelectedRows.shift());
        api.setNodesSelected({
          nodes: [latestSelectedNode],
          newValue: false,
          source: 'api',
        });
      }

      if (!arrayEquals<string>(newSelectedRows, previouslySelectedRows)) {
        const selections: SelectedCalculationsEvent[] = newSelectedRows.map(
          (nodeId) => ({
            nodeId,
            calculation: api.getRowNode(nodeId).data,
          })
        );

        // keep at least one item selected in minified version
        if (this.minified && selections?.length === 0) {
          const latestSelectedNode = api.getRowNode(
            previouslySelectedRows.shift()
          );

          api.setNodesSelected({
            nodes: [latestSelectedNode],
            newValue: true,
            source: 'api',
          });

          return;
        }

        this.selectionChange.emit(selections);
      }
    }, 0);
  }

  onGridStateUpdated(event: StateUpdatedEvent<Calculation[]>): void {
    if (
      !event.sources.some((source) =>
        AgGridStateService.IGNORED_EVENTS.has(source)
      )
    ) {
      this.agGridStateService.saveGridState(this.storageKey, event.state);
    }
  }

  onFirstDataRendered(): void {
    this.selectNodes();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    if (!this.isLoading) {
      this.gridApi.showNoRowsOverlay();
    }
  }

  private trackSelectedCalculation(): void {
    // Track only when Compare mode is active
    if (this.index !== undefined) {
      this.store
        .select(getSelectedReferenceTypeIdentifiers)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((referenceTypes) => {
          this.sameReferenceTypesSelected =
            referenceTypes[0] && referenceTypes[1]
              ? referenceTypes[0].materialNumber ===
                  referenceTypes[1].materialNumber &&
                referenceTypes[0].plant === referenceTypes[1].plant
              : false;
        });

      if (this.sameReferenceTypesSelected) {
        // User is comparing different Calculations of the same Material
        // Disable the checkbox for Calculation in the other index
        this.store
          .select(
            getSelectedCalculation({
              index: this.index === 0 ? 1 : 0,
            })
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((calculationFromOtherIndex) => {
            this.disabledCalculation = calculationFromOtherIndex;

            // AG Grid is not automatically refreshing the checkboxes when rows change unless the method is reassigned.
            // Grid API is undefined at this point
            this.rowSelection = {
              ...this.rowSelection,
              isRowSelectable: (row) => this.isRowSelectable(row),
            };
          });
      }
    }
  }

  private setTableProperties(minified: boolean): void {
    this.defaultColDef = { ...this.defaultColDef, floatingFilter: !minified };

    this.sideBar = minified
      ? undefined
      : this.sidebarService.DEFAULT_SIDE_BAR_CONFIG;
    this.statusBar = minified ? undefined : STATUS_BAR_CONFIG;

    this.components = minified
      ? FRAMEWORK_COMPONENTS_MINIFIED
      : FRAMEWORK_COMPONENTS_DEFAULT;

    this.cellSelection = !minified;
    this.rowGroupPanelShow = minified ? 'never' : 'always';

    this.rowSelection = {
      mode: this.rowSelectionType,
      checkboxes: (row) => !row.node.id.includes('row-group'),
      isRowSelectable: (row) => this.isRowSelectable(row),
      headerCheckbox: false,
      enableClickSelection: true,
      enableSelectionWithoutKeys: this.enableSelectionWithoutKeys,
    } as RowSelectionOptions;
  }

  private isRowSelectable(row: IRowNode): boolean {
    // Disable row selection for Calculation selected in the other index
    if (this.sameReferenceTypesSelected) {
      return (
        (row.data as Calculation).fingerprint !==
        this.disabledCalculation.fingerprint
      );
    }

    return true;
  }

  private selectNodes(): void {
    if (this.selectedNodeIds) {
      setTimeout(() => {
        let latestSelectedNodes: IRowNode[];

        if (this.selectedNodeIds.length === 2) {
          latestSelectedNodes = [
            this.gridApi.getRowNode(this.selectedNodeIds[0]),
            this.gridApi.getRowNode(this.selectedNodeIds[1]),
          ];
        } else {
          latestSelectedNodes = [
            this.gridApi.getRowNode(this.selectedNodeIds[0]),
          ];
          this.selectedNodeIds = this.selectedNodeIds.slice(1);
        }

        this.gridApi.setNodesSelected({
          nodes: latestSelectedNodes,
          newValue: true,
          source: 'api',
        });
      }, 0);
    }
  }
}
